// hooks/useOnboarding.ts — 1.24.00 first-run onboarding data (B1).
//
//   useTopics()                 GET /users/topics/ (falls back to core/data/feedTopics.ts)
//   useOnboardingSuggestions()  GET /users/suggested/?limit=12&for=onboarding — NOT
//                               filtered by the follow graph (unlike useSuggestedProfiles):
//                               step 2 keeps a just-followed card on screen as "Following"
//                               so the "n of 5" count has something to count.
//   useFollowMany()             POST /users/follow-many/ via followsService (store-synced)
//
// Components must NOT import usersService/followsService directly.

import { useCallback, useEffect, useState } from 'react';
import { usersService } from '@/core/services/usersService';
import { followsService } from '@/core/services/followsService';
import { FEED_TOPICS } from '@/core/data/feedTopics';
import type { SuggestedProfile, TopicOption } from '@/core/models/account';

export const ONBOARDING_MIN_INTERESTS = 3;
export const ONBOARDING_FOLLOW_TARGET = 5;
export const ONBOARDING_SUGGESTION_LIMIT = 12;

export interface TopicsHook {
  topics: TopicOption[];
  minInterests: number;
  loading: boolean;
}

export function useTopics(): TopicsHook {
  const [state, setState] = useState<TopicsHook>({
    topics: [],
    minInterests: ONBOARDING_MIN_INTERESTS,
    loading: true,
  });
  useEffect(() => {
    let alive = true;
    usersService.topics().then(
      (r) => {
        if (!alive) return;
        const topics = Array.isArray(r?.topics) && r.topics.length > 0 ? r.topics : FEED_TOPICS;
        const minInterests =
          typeof r?.minInterests === 'number' && r.minInterests > 0
            ? r.minInterests
            : ONBOARDING_MIN_INTERESTS;
        setState({ topics, minInterests, loading: false });
      },
      () => {
        // The picker must still work offline/on an old server — the 8 labels
        // live client-side too and the sync guard pins them to the backend.
        if (alive) setState({ topics: FEED_TOPICS, minInterests: ONBOARDING_MIN_INTERESTS, loading: false });
      },
    );
    return () => { alive = false; };
  }, []);
  return state;
}

export interface OnboardingSuggestionsHook {
  profiles: SuggestedProfile[];
  loading: boolean;
  error: string | null;
  /**
   * Re-ask the server. The `for=onboarding` ranking is `-interest_match` FIRST,
   * so it is only worth anything once the member's `interests` are saved — and
   * step 1 saves them AFTER this hook's mount fetch. Without a refetch between
   * the two, the server always annotates against an empty interest list, skips
   * the match entirely and answers with the plain follower-ranked list: the
   * whole ranking would be inert on every genuine first run.
   */
  refresh: () => void;
}

export function useOnboardingSuggestions(): OnboardingSuggestionsHook {
  const [state, setState] = useState<Omit<OnboardingSuggestionsHook, 'refresh'>>({
    profiles: [],
    loading: true,
    error: null,
  });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    // A refetch must show its loader — leaving the previous (unranked) list on
    // screen would read as "these are your interest-matched people" while the
    // real answer is still in flight.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flag
    setState((s) => ({ ...s, loading: true }));
    usersService.suggested({ limit: ONBOARDING_SUGGESTION_LIMIT, for: 'onboarding' }).then(
      (profiles) => { if (alive) setState({ profiles, loading: false, error: null }); },
      () => { if (alive) setState({ profiles: [], loading: false, error: 'Could not load suggestions.' }); },
    );
    return () => { alive = false; };
  }, [tick]);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, refresh };
}

/** Follow several handles at once. Resolves the server's outcome, or null on failure. */
export function useFollowMany(): (
  handles: string[],
) => Promise<{ followed: string[]; requested: string[]; skipped: string[] } | null> {
  return useCallback(async (handles: string[]) => {
    try {
      return await followsService.followMany(handles);
    } catch {
      return null;
    }
  }, []);
}
